import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Upload, Check, AlertCircle, Calendar, MapPin, DollarSign, User, Mail, Phone, Building, GraduationCap, Briefcase, FileText, Link as LinkIcon, Globe, Clock } from 'lucide-react';
import LogoPng from '../../assets/InitX_Logo.png';

const ApplicationContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%);
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const ApplicationForm = styled(motion.div)`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 3px solid #fb923c;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  padding: 40px;
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 12px;
    border-width: 2px;
  }
  
  @media (max-width: 480px) {
    padding: 15px;
    margin: 0 5px;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 20px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 15px;
    margin-bottom: 15px;
    border-radius: 8px;
  }
`;

const SectionTitle = styled.h3`
  color: #1e293b;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #fb923c;
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 15px;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
    margin-bottom: 12px;
    gap: 6px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || 'repeat(2, 1fr)'};
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #fb923c;
    box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.1);
  }
  
  &.error {
    border-color: #dc2626;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #fb923c;
    box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #fb923c;
    box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 10px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background: ${props => props.checked ? 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)' : '#f8fafc'};
  color: ${props => props.checked ? 'white' : '#374151'};
  border: 2px solid ${props => props.checked ? '#fb923c' : '#e5e7eb'};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #fb923c;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #fb923c 0%, #f97316 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 30px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(251, 146, 60, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ValidationMessage = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 4px;
  background: linear-gradient(90deg, #fb923c 0%, #f97316 100%);
  transition: width 0.3s ease;
  z-index: 10000;
`;

const JobApplication = () => {
  const [jobData, setJobData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    alternatePhone: '',
    dateOfBirth: '',
    gender: '',
    nationality: '',
    
    // Address Information
    currentAddress: '',
    permanentAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    
    // Professional Information
    currentJobTitle: '',
    currentCompany: '',
    totalExperience: '',
    relevantExperience: '',
    currentSalary: '',
    expectedSalary: '',
    noticePeriod: '',
    availableFrom: '',
    
    // Education
    highestEducation: '',
    university: '',
    graduationYear: '',
    cgpa: '',
    additionalCertifications: '',
    
    // 10th Standard
    tenth_board: '',
    tenth_school: '',
    tenth_year: '',
    tenth_percentage: '',
    
    // 12th Standard
    twelfth_board: '',
    twelfth_school: '',
    twelfth_year: '',
    twelfth_percentage: '',
    
    // Bachelor's Degree
    bachelor_university: '',
    bachelor_degree: '',
    bachelor_year: '',
    bachelor_cgpa: '',
    bachelor_percentage: '',
    
    // Technical Skills
    primarySkills: [],
    programmingLanguages: [],
    frameworks: [],
    databases: [],
    tools: [],
    
    // Work Preferences
    workType: '',
    preferredLocation: '',
    willingToRelocate: '',
    travelWillingness: '',
    
    // Documents & Links
    resume: null,
    coverLetter: '',
    portfolio: '',
    linkedinProfile: '',
    githubProfile: '',
    
    // References
    reference1Name: '',
    reference1Company: '',
    reference1Phone: '',
    reference1Email: '',
    reference2Name: '',
    reference2Company: '',
    reference2Phone: '',
    reference2Email: '',
    
    // Additional Information
    whyJoinUs: '',
    careerGoals: '',
    achievements: '',
    
    // Legal
    workAuthorization: '',
    criminalBackground: '',
    agreementTerms: false,
    dataConsent: false
  });

  const [uploadStatus, setUploadStatus] = useState('idle');
  const [errors, setErrors] = useState({});
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const jobParam = urlParams.get('job');
    if (jobParam) {
      try {
        const job = JSON.parse(decodeURIComponent(jobParam));
        setJobData(job);
      } catch (error) {
        console.error('Error parsing job data:', error);
      }
    }
  }, []);

  const getMinQualifications = (jobTitle) => {
    const qualifications = {
      'Senior UI/UX Designer': { tenth: 60, twelfth: 65, bachelor: 70 },
      'Frontend Developer': { tenth: 70, twelfth: 75, bachelor: 80 },
      'Product Manager': { tenth: 75, twelfth: 80, bachelor: 85 },
      'Marketing Specialist': { tenth: 60, twelfth: 65, bachelor: 70 },
      'Backend Developer': { tenth: 70, twelfth: 75, bachelor: 80 },
      'HTML Developer': { tenth: 60, twelfth: 65, bachelor: 70 }
    };
    return qualifications[jobTitle] || { tenth: 60, twelfth: 65, bachelor: 70 };
  };

  useEffect(() => {
    // Calculate form completion progress
    const totalFields = Object.keys(formData).length;
    const filledFields = Object.values(formData).filter(value => 
      value !== '' && value !== null && value !== false && 
      (Array.isArray(value) ? value.length > 0 : true)
    ).length;
    setProgress((filledFields / totalFields) * 100);
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSkillChange = (skillType, skill) => {
    setFormData(prev => ({
      ...prev,
      [skillType]: prev[skillType].includes(skill)
        ? prev[skillType].filter(s => s !== skill)
        : [...prev[skillType], skill]
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, resume: 'File size must be less than 5MB' }));
        return;
      }
      
      setUploadStatus('uploading');
      setTimeout(() => {
        setFormData(prev => ({ ...prev, resume: file }));
        setUploadStatus('success');
        setErrors(prev => ({ ...prev, resume: '' }));
      }, 1500);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    const minQuals = getMinQualifications(jobData?.title);
    
    if (step === 1) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.phone) newErrors.phone = 'Phone number is required';
      
      // Validate education percentages
      if (formData.tenth_percentage && parseFloat(formData.tenth_percentage) < minQuals.tenth) {
        newErrors.tenth_percentage = `Minimum ${minQuals.tenth}% required for 10th standard`;
      }
      if (formData.twelfth_percentage && parseFloat(formData.twelfth_percentage) < minQuals.twelfth) {
        newErrors.twelfth_percentage = `Minimum ${minQuals.twelfth}% required for 12th standard`;
      }
      if (formData.bachelor_cgpa && parseFloat(formData.bachelor_cgpa) < (minQuals.bachelor / 10)) {
        newErrors.bachelor_cgpa = `Minimum ${minQuals.bachelor / 10} CGPA required for bachelor's degree`;
      }
      if (formData.bachelor_percentage && parseFloat(formData.bachelor_percentage) < minQuals.bachelor) {
        newErrors.bachelor_percentage = `Minimum ${minQuals.bachelor}% required for bachelor's degree`;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isInternship = jobData?.department === 'Internship';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateStep(currentStep)) {
      const whatsappMessage = `Hi InitX Technologies!

New ${isInternship ? 'Internship' : 'Job'} Application:

*Position:* ${jobData?.title}
*Department:* ${jobData?.department}
*Location:* ${jobData?.location}

*Name:* ${formData.firstName} ${formData.lastName}
*Email:* ${formData.email}
*Phone:* ${formData.phone}
*Gender:* ${formData.gender}
${isInternship ? `*College:* ${formData.currentCompany}
*Year of Study:* ${formData.totalExperience}
*Duration Preference:* ${formData.noticePeriod}
*Available From:* ${formData.availableFrom}
*Stipend Expectation:* ${formData.expectedSalary}
*Prior Internship:* ${formData.currentJobTitle}` : `*Current Company:* ${formData.currentCompany}
*Experience:* ${formData.totalExperience}
*Notice Period:* ${formData.noticePeriod}
*Expected Salary:* ${formData.expectedSalary}`}

*10th:* ${formData.tenth_board} - ${formData.tenth_school} (${formData.tenth_year}) - ${formData.tenth_percentage}%
*12th:* ${formData.twelfth_board} - ${formData.twelfth_school} (${formData.twelfth_year}) - ${formData.twelfth_percentage}%
*Degree:* ${formData.bachelor_degree} - ${formData.bachelor_university} (${formData.bachelor_year}) - CGPA: ${formData.bachelor_cgpa}

*Skills:* ${formData.primarySkills.join(', ')}
*Work Preference:* ${formData.workType}
*Willing to Relocate:* ${formData.willingToRelocate}
*LinkedIn:* ${formData.linkedinProfile}
*Portfolio:* ${formData.portfolio}

*Why this role:* ${formData.coverLetter}
*Skills to develop:* ${formData.whyJoinUs}

Please review my application. Thank you!`;

      const whatsappURL = `https://wa.me/918986283243?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(whatsappURL, '_blank');
    }
  };

  const getJobSpecificSkills = (job) => {
    const skillSets = {
      'Senior UI/UX Designer': ['Figma', 'Adobe XD', 'Sketch', 'Photoshop', 'Illustrator', 'Prototyping', 'User Research', 'Wireframing'],
      'Frontend Developer': ['JavaScript', 'React', 'HTML/CSS', 'TypeScript', 'Vue.js', 'Angular', 'Sass/SCSS', 'Webpack'],
      'Product Manager': ['Product Strategy', 'Analytics', 'Roadmapping', 'Agile/Scrum', 'User Stories', 'Market Research', 'A/B Testing', 'Jira'],
      'Marketing Specialist': ['Digital Marketing', 'SEO/SEM', 'Google Analytics', 'Social Media', 'Content Marketing', 'Email Marketing', 'PPC', 'Copywriting'],
      'Backend Developer': ['Node.js', 'Python', 'Java', 'Express.js', 'MongoDB', 'PostgreSQL', 'REST APIs', 'Docker'],
      'Frontend Development Intern': ['HTML', 'CSS', 'JavaScript', 'React', 'Git', 'Responsive Design'],
      'Architectural Design Intern': ['AutoCAD', 'Civil Design', 'Architecture', '2D/3D Drawing', 'Structural Planning'],
      'Digital Marketing Intern': ['SEO', 'Social Media', 'Content Writing', 'Google Analytics', 'Canva']
    };
    return skillSets[job?.title] || [];
  };

  const programmingLanguages = ['JavaScript', 'Python', 'Java', 'C++', 'C#', 'PHP', 'Ruby', 'Go', 'TypeScript', 'Swift', 'Kotlin'];
  const frameworks = ['React', 'Angular', 'Vue.js', 'Node.js', 'Express.js', 'Django', 'Flask', 'Spring Boot', 'Laravel'];
  const databases = ['MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Oracle', 'SQL Server', 'SQLite'];
  const tools = ['Git', 'Docker', 'Kubernetes', 'Jenkins', 'AWS', 'Azure', 'Google Cloud', 'Figma', 'Adobe XD'];

  if (!jobData) {
    return (
      <ApplicationContainer>
        <div style={{ textAlign: 'center', color: 'white', padding: '100px 0' }}>
          <h2>Loading application form...</h2>
        </div>
      </ApplicationContainer>
    );
  }

  return (
    <ApplicationContainer>
      <ProgressBar style={{ width: `${progress}%` }} />
      
      <ApplicationForm
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div style={{ position: 'absolute', top: '0', left: '0', right: '0', height: '6px', background: 'linear-gradient(90deg, #fb923c 0%, #f97316 50%, #fb923c 100%)', borderRadius: '20px 20px 0 0' }}></div>
        
        {/* Header */}
          <div style={{ textAlign: 'left', marginBottom: '30px', paddingBottom: '20px', borderBottom: '2px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img src={LogoPng} alt="InitX" style={{ height: '50px', width: 'auto' }} />
              <span style={{ color: '#1e293b', fontSize: '24px', fontWeight: '700', fontFamily: 'Inter, sans-serif' }}>InitX</span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#64748b', fontSize: '12px' }}>Application Progress</div>
              <div style={{ color: '#fb923c', fontSize: '18px', fontWeight: '600' }}>{Math.round(progress)}%</div>
            </div>
          </div>
          
          <h2 style={{ marginBottom: '8px', color: '#1e293b', fontSize: '28px', fontWeight: '700' }}>{isInternship ? 'Internship Application Form' : 'Job Application Form'}</h2>
          <div style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', padding: '15px 20px', borderRadius: '12px', border: '1px solid #0ea5e9' }}>
            <h3 style={{ marginBottom: '5px', color: '#fb923c', fontSize: '20px', fontWeight: '600' }}>{jobData.title}</h3>
            <p style={{ marginBottom: '0', color: '#64748b', fontSize: '14px' }}>{jobData.department} • {jobData.location} • {jobData.salary}</p>
          </div>
          
          {/* Minimum Requirements Display */}
          <div style={{ background: '#f1f5f9', padding: '20px', borderRadius: '12px', marginTop: '20px', border: '1px solid #e2e8f0' }}>
            <h4 style={{ color: '#1e293b', marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>📊 Minimum Academic Requirements</h4>
            <div className="requirements-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '15px' }}>
              <div style={{ background: '#f0f9ff', padding: '12px', borderRadius: '8px', border: '1px solid #0ea5e9', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', marginBottom: '5px' }}>🎓</div>
                <div style={{ color: '#0369a1', fontSize: '12px', fontWeight: '600' }}>10th Standard</div>
                <div style={{ color: '#1e40af', fontSize: '16px', fontWeight: '700' }}>{getMinQualifications(jobData.title).tenth}%+</div>
              </div>
              <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px', border: '1px solid #22c55e', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', marginBottom: '5px' }}>📚</div>
                <div style={{ color: '#15803d', fontSize: '12px', fontWeight: '600' }}>12th Standard</div>
                <div style={{ color: '#166534', fontSize: '16px', fontWeight: '700' }}>{getMinQualifications(jobData.title).twelfth}%+</div>
              </div>
              <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', border: '1px solid #f59e0b', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', marginBottom: '5px' }}>🎯</div>
                <div style={{ color: '#d97706', fontSize: '12px', fontWeight: '600' }}>Bachelor's Degree</div>
                <div style={{ color: '#92400e', fontSize: '16px', fontWeight: '700' }}>{getMinQualifications(jobData.title).bachelor}%+</div>
              </div>
            </div>
            <div style={{ background: '#fef2f2', padding: '12px', borderRadius: '8px', border: '1px solid #f87171' }}>
              <p style={{ color: '#dc2626', fontSize: '13px', margin: '0', fontWeight: '500' }}>
                ⚠️ <strong>Important:</strong> Applications not meeting minimum academic requirements will be automatically rejected during initial screening.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <Section>
            <SectionTitle>
              <User size={20} />
              Personal Information
            </SectionTitle>
            
            <FormRow>
              <FormGroup>
                <Label>First Name *</Label>
                <Input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={errors.firstName ? 'error' : ''}
                  placeholder="John"
                  required
                />
                {errors.firstName && (
                  <ValidationMessage>
                    <AlertCircle size={14} />
                    {errors.firstName}
                  </ValidationMessage>
                )}
              </FormGroup>
              
              <FormGroup>
                <Label>Last Name *</Label>
                <Input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={errors.lastName ? 'error' : ''}
                  placeholder="Doe"
                  required
                />
                {errors.lastName && (
                  <ValidationMessage>
                    <AlertCircle size={14} />
                    {errors.lastName}
                  </ValidationMessage>
                )}
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="john.doe@example.com"
                  required
                />
                {errors.email && (
                  <ValidationMessage>
                    <AlertCircle size={14} />
                    {errors.email}
                  </ValidationMessage>
                )}
              </FormGroup>
              
              <FormGroup>
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={errors.phone ? 'error' : ''}
                  placeholder="+1 (555) 123-4567"
                  required
                />
                {errors.phone && (
                  <ValidationMessage>
                    <AlertCircle size={14} />
                    {errors.phone}
                  </ValidationMessage>
                )}
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Gender</Label>
                <Select name="gender" value={formData.gender} onChange={handleInputChange}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </Select>
              </FormGroup>
            </FormRow>
          </Section>

          {/* Professional / Internship Information */}
          <Section>
            <SectionTitle>
              <Briefcase size={20} />
              {isInternship ? 'Internship Details' : 'Professional Information'}
            </SectionTitle>

            {isInternship ? (
              <>
                <FormRow>
                  <FormGroup>
                    <Label>Current College/University *</Label>
                    <Input type="text" name="currentCompany" value={formData.currentCompany} onChange={handleInputChange} placeholder="e.g. IIT Delhi, VIT Vellore" required />
                  </FormGroup>
                  <FormGroup>
                    <Label>Current Year of Study *</Label>
                    <Select name="totalExperience" value={formData.totalExperience} onChange={handleInputChange} required>
                      <option value="">Select Year</option>
                      <option value="1st Year">1st Year</option>
                      <option value="2nd Year">2nd Year</option>
                      <option value="3rd Year">3rd Year</option>
                      <option value="4th Year">4th Year</option>
                      <option value="Final Year">Final Year</option>
                      <option value="Recently Graduated">Recently Graduated</option>
                      <option value="Other">Other</option>
                    </Select>
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <Label>Internship Duration Preference *</Label>
                    <Select name="noticePeriod" value={formData.noticePeriod} onChange={handleInputChange} required>
                      <option value="">Select Duration</option>
                      <option value="1 Month">1 Month</option>
                      <option value="2 Months">2 Months</option>
                      <option value="3 Months">3 Months</option>
                      <option value="6 Months">6 Months</option>
                      <option value="Other">Other</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Available From *</Label>
                    <Input type="date" name="availableFrom" value={formData.availableFrom} onChange={handleInputChange} required />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <Label>Stipend Expectation (Monthly)</Label>
                    <Input type="text" name="expectedSalary" value={formData.expectedSalary} onChange={handleInputChange} placeholder="e.g. ₹5,000 - ₹8,000" />
                  </FormGroup>
                  <FormGroup>
                    <Label>Prior Internship Experience?</Label>
                    <Select name="currentJobTitle" value={formData.currentJobTitle} onChange={handleInputChange}>
                      <option value="">Select</option>
                      <option value="No prior experience">No, this is my first internship</option>
                      <option value="1 internship">Yes, 1 internship</option>
                      <option value="2+ internships">Yes, 2 or more internships</option>
                      <option value="Other">Other</option>
                    </Select>
                  </FormGroup>
                </FormRow>
              </>
            ) : (
              <>
                <FormRow>
                  <FormGroup>
                    <Label>Current Job Title</Label>
                    <Input type="text" name="currentJobTitle" value={formData.currentJobTitle} onChange={handleInputChange} placeholder="Senior Software Engineer" />
                  </FormGroup>
                  <FormGroup>
                    <Label>Current Company</Label>
                    <Input type="text" name="currentCompany" value={formData.currentCompany} onChange={handleInputChange} placeholder="Tech Corp Inc." />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <Label>Total Experience *</Label>
                    <Select name="totalExperience" value={formData.totalExperience} onChange={handleInputChange} required>
                      <option value="">Select Experience</option>
                      <option value="0-1">0-1 years</option>
                      <option value="1-3">1-3 years</option>
                      <option value="3-5">3-5 years</option>
                      <option value="5-10">5-10 years</option>
                      <option value="10+">10+ years</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Notice Period *</Label>
                    <Select name="noticePeriod" value={formData.noticePeriod} onChange={handleInputChange} required>
                      <option value="">Select Notice Period</option>
                      <option value="Immediate">Immediate</option>
                      <option value="15 days">15 days</option>
                      <option value="1 month">1 month</option>
                      <option value="2 months">2 months</option>
                      <option value="3 months">3 months</option>
                    </Select>
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <Label>Current Salary (Annual)</Label>
                    <Input type="text" name="currentSalary" value={formData.currentSalary} onChange={handleInputChange} placeholder="$80,000" />
                  </FormGroup>
                  <FormGroup>
                    <Label>Expected Salary (Annual) *</Label>
                    <Input type="text" name="expectedSalary" value={formData.expectedSalary} onChange={handleInputChange} placeholder="$90,000" required />
                  </FormGroup>
                </FormRow>
              </>
            )}
          </Section>

          {/* Education Details */}
          <Section>
            <SectionTitle>
              <GraduationCap size={20} />
              Education Details
            </SectionTitle>
            
            {/* 10th Standard */}
            <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
              <h4 style={{ color: '#1e293b', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>10th Standard</h4>
              <FormRow>
                <FormGroup>
                  <Label>Board *</Label>
                  <Input
                    type="text"
                    name="tenth_board"
                    value={formData.tenth_board}
                    onChange={handleInputChange}
                    placeholder="CBSE / ICSE / State Board"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>School Name *</Label>
                  <Input
                    type="text"
                    name="tenth_school"
                    value={formData.tenth_school}
                    onChange={handleInputChange}
                    placeholder="School Name"
                    required
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <Label>Passing Year *</Label>
                  <Input
                    type="number"
                    name="tenth_year"
                    value={formData.tenth_year}
                    onChange={handleInputChange}
                    placeholder="2018"
                    min="1990"
                    max="2030"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Percentage *</Label>
                  <Input
                    type="number"
                    name="tenth_percentage"
                    value={formData.tenth_percentage}
                    onChange={handleInputChange}
                    className={errors.tenth_percentage ? 'error' : ''}
                    placeholder="85.5"
                    min="0"
                    max="100"
                    step="0.1"
                    required
                  />
                  {errors.tenth_percentage && (
                    <ValidationMessage>
                      <AlertCircle size={14} />
                      {errors.tenth_percentage}
                    </ValidationMessage>
                  )}
                </FormGroup>
              </FormRow>
            </div>
            
            {/* 12th Standard */}
            <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
              <h4 style={{ color: '#1e293b', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>12th Standard</h4>
              <FormRow>
                <FormGroup>
                  <Label>Board *</Label>
                  <Input
                    type="text"
                    name="twelfth_board"
                    value={formData.twelfth_board}
                    onChange={handleInputChange}
                    placeholder="CBSE / ICSE / State Board"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>School/College Name *</Label>
                  <Input
                    type="text"
                    name="twelfth_school"
                    value={formData.twelfth_school}
                    onChange={handleInputChange}
                    placeholder="School/College Name"
                    required
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <Label>Passing Year *</Label>
                  <Input
                    type="number"
                    name="twelfth_year"
                    value={formData.twelfth_year}
                    onChange={handleInputChange}
                    placeholder="2020"
                    min="1990"
                    max="2030"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Percentage *</Label>
                  <Input
                    type="number"
                    name="twelfth_percentage"
                    value={formData.twelfth_percentage}
                    onChange={handleInputChange}
                    className={errors.twelfth_percentage ? 'error' : ''}
                    placeholder="78.2"
                    min="0"
                    max="100"
                    step="0.1"
                    required
                  />
                  {errors.twelfth_percentage && (
                    <ValidationMessage>
                      <AlertCircle size={14} />
                      {errors.twelfth_percentage}
                    </ValidationMessage>
                  )}
                </FormGroup>
              </FormRow>
            </div>
            
            {/* Bachelor's Degree */}
            <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
              <h4 style={{ color: '#1e293b', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>Bachelor's Degree</h4>
              <FormRow>
                <FormGroup>
                  <Label>University *</Label>
                  <Input
                    type="text"
                    name="bachelor_university"
                    value={formData.bachelor_university}
                    onChange={handleInputChange}
                    placeholder="University Name"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Degree *</Label>
                  <Select name="bachelor_degree" value={formData.bachelor_degree} onChange={handleInputChange} required>
                    <option value="">Select Degree</option>
                    <option value="B.Tech">B.Tech</option>
                    <option value="B.E">B.E</option>
                    <option value="BCA">BCA</option>
                    <option value="B.Sc">B.Sc</option>
                    <option value="BBA">BBA</option>
                    <option value="B.Com">B.Com</option>
                    <option value="BA">BA</option>
                    <option value="Other">Other</option>
                  </Select>
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <Label>Passing Year *</Label>
                  <Input
                    type="number"
                    name="bachelor_year"
                    value={formData.bachelor_year}
                    onChange={handleInputChange}
                    placeholder="2024"
                    min="1990"
                    max="2030"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>CGPA *</Label>
                  <Input
                    type="number"
                    name="bachelor_cgpa"
                    value={formData.bachelor_cgpa}
                    onChange={handleInputChange}
                    className={errors.bachelor_cgpa ? 'error' : ''}
                    placeholder="8.5"
                    min="0"
                    max="10"
                    step="0.01"
                    required
                  />
                  {errors.bachelor_cgpa && (
                    <ValidationMessage>
                      <AlertCircle size={14} />
                      {errors.bachelor_cgpa}
                    </ValidationMessage>
                  )}
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <Label>Percentage (if available)</Label>
                <Input
                  type="number"
                  name="bachelor_percentage"
                  value={formData.bachelor_percentage}
                  onChange={handleInputChange}
                  className={errors.bachelor_percentage ? 'error' : ''}
                  placeholder="75.5"
                  min="0"
                  max="100"
                  step="0.1"
                />
                {errors.bachelor_percentage && (
                  <ValidationMessage>
                    <AlertCircle size={14} />
                    {errors.bachelor_percentage}
                  </ValidationMessage>
                )}
              </FormGroup>
            </div>
          </Section>
          <Section>
            <SectionTitle>
              <FileText size={20} />
              Technical Skills
            </SectionTitle>
            
            <FormGroup>
              <Label>Primary Skills for {jobData.title} *</Label>
              <CheckboxGroup>
                {getJobSpecificSkills(jobData).map(skill => (
                  <CheckboxLabel key={skill} checked={formData.primarySkills.includes(skill)}>
                    <input
                      type="checkbox"
                      checked={formData.primarySkills.includes(skill)}
                      onChange={() => handleSkillChange('primarySkills', skill)}
                      style={{ display: 'none' }}
                    />
                    <div style={{ 
                      width: '16px', 
                      height: '16px', 
                      borderRadius: '3px', 
                      background: formData.primarySkills.includes(skill) ? 'white' : 'transparent',
                      border: formData.primarySkills.includes(skill) ? 'none' : '2px solid #d1d5db',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      color: '#fb923c'
                    }}>
                      {formData.primarySkills.includes(skill) && '✓'}
                    </div>
                    {skill}
                  </CheckboxLabel>
                ))}
              </CheckboxGroup>
            </FormGroup>

            {!isInternship && (
              <FormGroup>
                <Label>Programming Languages</Label>
                <CheckboxGroup>
                  {programmingLanguages.map(lang => (
                    <CheckboxLabel key={lang} checked={formData.programmingLanguages.includes(lang)}>
                      <input type="checkbox" checked={formData.programmingLanguages.includes(lang)} onChange={() => handleSkillChange('programmingLanguages', lang)} style={{ display: 'none' }} />
                      <div style={{ width: '16px', height: '16px', borderRadius: '3px', background: formData.programmingLanguages.includes(lang) ? 'white' : 'transparent', border: formData.programmingLanguages.includes(lang) ? 'none' : '2px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fb923c' }}>
                        {formData.programmingLanguages.includes(lang) && '✓'}
                      </div>
                      {lang}
                    </CheckboxLabel>
                  ))}
                </CheckboxGroup>
              </FormGroup>
            )}
          </Section>

          {/* Work Preferences */}
          <Section>
            <SectionTitle>
              <Globe size={20} />
              Work Preferences
            </SectionTitle>
            
            <FormRow>
              <FormGroup>
                <Label>Work Type Preference *</Label>
                <Select name="workType" value={formData.workType} onChange={handleInputChange} required>
                  <option value="">Select Work Type</option>
                  <option value="Remote">Remote Only</option>
                  <option value="Onsite">On-site Only</option>
                  <option value="Hybrid">Hybrid (Remote + On-site)</option>
                  <option value="Flexible">Flexible</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Willing to Relocate?</Label>
                <Select name="willingToRelocate" value={formData.willingToRelocate} onChange={handleInputChange}>
                  <option value="">Select Option</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="Depends on location">Depends on location</option>
                </Select>
              </FormGroup>
            </FormRow>
          </Section>

          {/* Documents & Portfolio */}
          <Section>
            <SectionTitle>
              <Upload size={20} />
              Documents & Portfolio
            </SectionTitle>
            
            <FormGroup>
              <Label>Resume/CV *</Label>
              <div style={{
                border: uploadStatus === 'success' ? '2px solid #22c55e' : '2px dashed #cbd5e1',
                borderRadius: '12px',
                padding: '30px',
                textAlign: 'center',
                background: uploadStatus === 'success' ? '#f0fdf4' : '#f8fafc',
                cursor: uploadStatus === 'uploading' ? 'not-allowed' : 'pointer'
              }}>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  required
                  disabled={uploadStatus === 'uploading'}
                  style={{ display: 'none' }}
                  id="resume-upload"
                />
                <label htmlFor="resume-upload" style={{ cursor: uploadStatus === 'uploading' ? 'not-allowed' : 'pointer' }}>
                  {uploadStatus === 'idle' && (
                    <>
                      <Upload size={48} style={{ color: '#64748b', marginBottom: '10px' }} />
                      <p style={{ color: '#64748b', marginBottom: '5px' }}>Click to upload your resume</p>
                      <small style={{ color: '#94a3b8' }}>PDF, DOC, DOCX (Max 5MB)</small>
                    </>
                  )}
                  {uploadStatus === 'uploading' && (
                    <>
                      <Clock size={48} style={{ color: '#fb923c', marginBottom: '10px' }} />
                      <p style={{ color: '#fb923c', marginBottom: '5px', fontWeight: '600' }}>Uploading your resume...</p>
                    </>
                  )}
                  {uploadStatus === 'success' && formData.resume && (
                    <>
                      <Check size={48} style={{ color: '#15803d', marginBottom: '10px' }} />
                      <p style={{ color: '#15803d', marginBottom: '5px', fontWeight: '600' }}>Resume uploaded successfully!</p>
                      <p style={{ color: '#374151', fontSize: '14px' }}>📎 {formData.resume.name}</p>
                    </>
                  )}
                </label>
              </div>
              {errors.resume && (
                <ValidationMessage>
                  <AlertCircle size={14} />
                  {errors.resume}
                </ValidationMessage>
              )}
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>LinkedIn Profile</Label>
                <Input
                  type="url"
                  name="linkedinProfile"
                  value={formData.linkedinProfile}
                  onChange={handleInputChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Portfolio/GitHub</Label>
                <Input
                  type="url"
                  name="portfolio"
                  value={formData.portfolio}
                  onChange={handleInputChange}
                  placeholder="https://github.com/yourusername"
                />
              </FormGroup>
            </FormRow>
          </Section>

          {/* Cover Letter & Additional Info */}
          <Section>
            <SectionTitle>
              <FileText size={20} />
              Additional Information
            </SectionTitle>
            
            <FormGroup>
              <Label>{isInternship ? 'Why do you want this internship? *' : 'Cover Letter *'}</Label>
              <TextArea
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleInputChange}
                placeholder={isInternship ? 'Tell us what excites you about this internship and what you hope to learn...' : "Tell us why you're the perfect fit for this role. Highlight your relevant experience, skills, and what excites you about this opportunity..."}
                required
              />
            </FormGroup>

            {isInternship && (
              <FormGroup>
                <Label>What skills do you want to develop during this internship?</Label>
                <TextArea
                  name="whyJoinUs"
                  value={formData.whyJoinUs}
                  onChange={handleInputChange}
                  placeholder="e.g. I want to improve my AutoCAD skills and learn real-world structural design..."
                />
              </FormGroup>
            )}

            {!isInternship && (
              <FormGroup>
                <Label>Why do you want to join InitX?</Label>
                <TextArea
                  name="whyJoinUs"
                  value={formData.whyJoinUs}
                  onChange={handleInputChange}
                  placeholder="What attracts you to our company and this role?"
                />
              </FormGroup>
            )}
          </Section>

          {/* Legal & Consent */}
          <Section>
            <SectionTitle>
              <Check size={20} />
              Legal & Consent
            </SectionTitle>
            
            {isInternship ? (
              <FormGroup>
                <Label>Are you currently enrolled in a college/university? *</Label>
                <Select name="workAuthorization" value={formData.workAuthorization} onChange={handleInputChange} required>
                  <option value="">Select</option>
                  <option value="Yes, currently enrolled">Yes, currently enrolled</option>
                  <option value="Recently graduated">No, recently graduated</option>
                  <option value="Gap year">On a gap year</option>
                  <option value="Other">Other</option>
                </Select>
              </FormGroup>
            ) : (
              <FormGroup>
                <Label>Work Authorization *</Label>
                <Select name="workAuthorization" value={formData.workAuthorization} onChange={handleInputChange} required>
                  <option value="">Select Authorization Status</option>
                  <option value="Indian Citizen">Indian Citizen</option>
                  <option value="Need Sponsorship">Need Sponsorship</option>
                  <option value="Other">Other</option>
                </Select>
              </FormGroup>
            )}

            <FormGroup style={{ marginTop: '30px' }}>
              <CheckboxLabel checked={formData.agreementTerms}>
                <input
                  type="checkbox"
                  name="agreementTerms"
                  checked={formData.agreementTerms}
                  onChange={handleInputChange}
                  required
                  style={{ display: 'none' }}
                />
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  borderRadius: '4px', 
                  background: formData.agreementTerms ? 'white' : 'transparent',
                  border: formData.agreementTerms ? 'none' : '2px solid #d1d5db',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: '#fb923c'
                }}>
                  {formData.agreementTerms && '✓'}
                </div>
                I agree to the terms and conditions and privacy policy *
              </CheckboxLabel>
            </FormGroup>

            <FormGroup>
              <CheckboxLabel checked={formData.dataConsent}>
                <input
                  type="checkbox"
                  name="dataConsent"
                  checked={formData.dataConsent}
                  onChange={handleInputChange}
                  required
                  style={{ display: 'none' }}
                />
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  borderRadius: '4px', 
                  background: formData.dataConsent ? 'white' : 'transparent',
                  border: formData.dataConsent ? 'none' : '2px solid #d1d5db',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: '#fb923c'
                }}>
                  {formData.dataConsent && '✓'}
                </div>
                I consent to the processing of my personal data for recruitment purposes *
              </CheckboxLabel>
            </FormGroup>
          </Section>

          <SubmitButton type="submit">
            🚀 Submit Application via WhatsApp
          </SubmitButton>

          <div style={{ textAlign: 'center', marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <p style={{ color: '#64748b', fontSize: '12px', margin: '0', lineHeight: '1.5' }}>
              🔒 <strong>Secure Application:</strong> Your information is encrypted and handled according to our privacy policy. 
              We respect your privacy and will only use this information for recruitment purposes.
            </p>
          </div>
        </form>
        
        <style jsx>{`
          @media (max-width: 768px) {
            .requirements-grid {
              grid-template-columns: 1fr !important;
              gap: 10px !important;
            }
            
            h2 {
              font-size: 24px !important;
            }
            
            h3 {
              font-size: 18px !important;
            }
            
            h4 {
              font-size: 16px !important;
            }
          }
          
          @media (max-width: 480px) {
            .requirements-grid {
              gap: 8px !important;
            }
            
            h2 {
              font-size: 20px !important;
            }
            
            h3 {
              font-size: 16px !important;
            }
            
            h4 {
              font-size: 14px !important;
            }
          }
        `}</style>
      </ApplicationForm>
    </ApplicationContainer>
  );
};

export default JobApplication;